import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Image,
  Stack,
  Icon,
  VStack,
  Input,
  Select,
  Circle,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormControl,
  FormErrorMessage,
  useBreakpointValue,
  Divider,
  useToast,
  Tooltip,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import axiosInstance from "../helpers/axiosInstance";
import { FaCheckCircle } from "react-icons/fa";
import aiCreationImage from "../assets/images/for-creater-blogger.jpg";
import createrProgramImage from "../assets/images/for-creater-program.jpg";
import Navbar from "../components/Navbar";
import darkLogo from "../assets/images/Newlogo-black.png";
import BackToTopButton from "../components/common/BackToTopButton";
import CreatorsBusinessBanner from "../components/common/CreatorsBusinessBanner";
import Footer from "./Footer";
import FAQCreatorsPage from "./FAQCreatorsPage";
import Translate from "../components/LanguageTranslation/Translate";
import banner from "../assets/images/for-creator.jpg";
import PhoneInputComponent from "./common/PhoneInputComponent";
import JoinCommunitySection from "./common/JoinCommunitySection";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../auth";
import { useGoogleAuth } from "./common/google/GoogleAuthContext";
import AuthModal from "./AuthModal";

const CreatorPage = () => {
  const toast = useToast();
  
  // Authentication state
  const [logged] = useAuth();
  const {
    authState: { isLoggedIn },
  } = useGoogleAuth();
  const isUserLoggedIn = logged || isLoggedIn;
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  
  // State variables to track form inputs
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [websiteOrInstagram, setWebsiteOrInstagram] = useState("");
  const [phone, setPhone] = useState(""); // Add state for phone
  // const [questionAnswer, setQuestionAnswer] = useState("");
  const [creator, setCreator] = useState("");
  const [loading, setLoading] = useState(false);

  // Create refs for the sections you want to scroll to
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null); // New ref for section 3

  const scrollToSection1 = () => {
    section1Ref.current.scrollIntoView({ behavior: "smooth" });
  };

  // Function to handle scrolling to section 2
  const scrollToSection2 = () => {
    section1Ref.current.scrollIntoView({ behavior: "smooth" });
  };

  // Function to handle scrolling to section 3
  const scrollToSection3 = () => {
    section3Ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const showBreak = useBreakpointValue({ base: false, md: true });

  const [errors, setErrors] = useState({});

  // Auto-fill email from localStorage when component mounts
  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Handle auth modal close
  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
  };

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex =
      /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;

    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      newErrors.email = "Invalid email format";
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(phone)) {
      newErrors.phone = "Invalid phone number format";
    }
    if (!creator) newErrors.creator = "Creator type is required";
    if (!websiteOrInstagram.trim() || !isValidURL(websiteOrInstagram))
      newErrors.websiteOrInstagram = "Website/Instagram is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Check if user is logged in first
    if (!isUserLoggedIn) {
      setAuthModalOpen(true);
      toast({
        title: "Authentication Required",
        description: "Please log in to submit the creator application form.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setLoading(true);
    try {
      const payload = {
        name,
        email,
        websiteOrInstagram,
        phone,
        creator,
        type: "Creator",
        application_date: new Date().toISOString().slice(0, 10),
      };

      const response = await axiosInstance.post("/userform/userforms", payload);

      if (response.status === 201) {
        setWebsiteOrInstagram("");
        setEmail("");
        setName("");
        setCreator("");
        setPhone("");
        setLoading(false);
        toast({
          description: response?.data?.message,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      setLoading(false);
      console.error("Error submitting form:", error);
      toast({
        description: error?.response?.data?.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Build Your Online Travel Career&nbsp;| NOMAD360 for Outdoor Creators
        </title>
        <meta
          name="description"
          content="Build your online travel career with NOMAD360. For outdoor creators ready to grow their brand, connect, and earn online."
        />
        <meta
          name="description:mobile"
          content="Build your online travel career with NOMAD360. For outdoor creators ready to grow, connect, and earn."
        />
      </Helmet>

      <Box position="absolute" top={0} left={0} width="100%" zIndex="2">
        <Navbar
          bgColor="white"
          linkColor="black"
          styling="outline"
          logoSrc={darkLogo}
          userProfileBgColor="transparaent"
          languageCodeColor="black"
        />
        <CreatorsBusinessBanner heading="For Creators" banner={banner} />
        <Box bg="white" px={{ base: 4, md: 12 }} py={8}>
          {/* Section 1: Sales Growth (Image on Right, Text on Left) */}
          <Flex
            direction={{ base: "column-reverse", md: "row" }}
            mb={{ base: 20, md: 20 }}
            maxW={{ base: "auto", md: "80vw", lg: "80vw", xl: "80vw" }}
            justifyContent="center"
            mx="auto"
            h={{ base: "auto", md: "auto", lg: "auto", xl: "auto" }} // Responsive height
          >
            <VStack spacing={0}>
              <Flex
                ref={section1Ref}
                w="100%"
                // bg="#EBEAF2"
                h={{ base: "auto", md: "auto", lg: "auto", xl: "auto" }}
                alignItems="flex-start"
                justify="space-between"
                direction={{ base: "column", md: "row" }}
              >
                {/* Text Section */}
                <Box
                  flex="1"
                  borderRadius="lg"
                  p={{ base: 6, md: 6 }}
                  pl={{ base: 6, md: 6 }}
                  pb={{ base: 6, md: 24 }}
                  pt={0}
                  textAlign={{ base: "center", md: "left" }}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  h="100%"
                >
                  <Heading
                    fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                    w={{ base: "100%", md: "100%", lg: "80%" }}
                    textAlign="left"
                  >
                    <Translate text="Your Online" />{" "}
                    <Text
                      as="span"
                      bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                      bgClip="text"
                      fontWeight="bold"
                    >
                      <Translate text="Travel Career" />
                    </Text>
                  </Heading>
                  <Text
                    mt={{ base: 2, md: 4 }}
                    fontSize={{ base: "md", md: "lg" }}
                    textAlign="left"
                    color="gray.600"
                  >
                    <Translate text="Take your passion for travel to the next level with an exciting online career! Join our platform to grow your brand, share your adventures, and discover endless opportunities to grow as a travel creator." />
                  </Text>
                  {/* <Stack direction="row" spacing={4} mt={{ base: 2, md: 6 }}>
                  <Button
                    bg="black"
                    color="white"
                    _hover="none"
                    size={{ base: "sm", md: "lg" }}
                    onClick={scrollToSection3} // Scroll to section 3 on click
                  >
                    <Translate text="Get Started" />
                  </Button>
                  <Button
                    border="1px"
                    borderColor="black"
                    bg="none"
                    _hover="none"
                    size={{ base: "sm", md: "lg" }}
                    onClick={scrollToSection2} // Scroll to section 2 on click
                  >
                    <Translate text="Learn More" />
                  </Button>
                </Stack> */}
                </Box>

                {/* Form Section */}
                <Box
                  flex="1"
                  borderRadius="xl"
                  overflow="hidden"
                  h="100%"
                  w="100%"
                  minH={{ base: "300px", md: "100%" }} // Responsive min-height
                  bgPosition="center"
                  bgSize="cover"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {/* Semi-transparent background overlay for form */}
                  <Box
                    bg="rgba(255, 255, 255, 0.85)"
                    p={{ base: 6, md: 6 }}
                    pl={{ base: 6, md: 0 }}
                    pb={2}
                    borderRadius="lg"
                    boxShadow="lg"
                    w="100%"
                    h="100%"
                  >
                    <VStack
                      spacing={{ base: "2", md: "1.5" }}
                      align="flex-start"
                      w="100%"
                    >
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Name" />
                      </Text>
                      <FormControl isInvalid={errors.name}>
                        <Input
                          placeholder={Translate({
                            text: "Your name",
                            as: "string",
                          })}
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        bg="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Flex align="center">
                        <Text fontWeight="bold" color="black" mb={0}>
                          <Translate text="Email" />
                        </Text>
                        <Tooltip
                          hasArrow
                          placement="right"
                          label={
                            <Box maxW="xs">
                              <Text mb={0}>
                                <Translate text="If you want to create a creator account with a different email, please sign up with another email address." />
                              </Text>
                            </Box>
                          }
                        >
                          <Box aria-label="Email info" color="gray.500" cursor="pointer" ml={2}>
                            <InfoOutlineIcon />
                          </Box>
                        </Tooltip>
                      </Flex>
                      <FormControl isInvalid={errors.email}>
                        <Input
                          placeholder={Translate({
                            text: "Your email address",
                            as: "string",
                          })}
                          borderColor="gray.300"
                          bg={localStorage.getItem("email") ? "gray.100" : "white"}
                          w="100%"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          isDisabled={!!localStorage.getItem("email")}
                          cursor={localStorage.getItem("email") ? "not-allowed" : "text"}
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        bg="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Phone Number" />
                      </Text>
                      <FormControl isInvalid={errors.phone}>
                        <PhoneInputComponent
                          phone={phone}
                          setPhone={setPhone}
                        />
                        <FormErrorMessage>{errors.phone}</FormErrorMessage>
                      </FormControl>

                      <Divider
                        m={1}
                        bg="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Creator Type" />
                      </Text>
                      <FormControl isInvalid={errors.creator}>
                        <Select
                          placeholder={Translate({
                            text: "Select your primary platform",
                            as: "string",
                          })}
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={creator}
                          onChange={(e) => setCreator(e.target.value)}
                        >
                          <option value="Blogs">
                            <Translate text="Blogs" />
                          </option>
                          <option value="Newsletters">
                            <Translate text="Newsletters" />
                          </option>
                          <option value="Instagram">
                            <Translate text="Instagram" />
                          </option>
                          <option value="TikTok">
                            <Translate text="TikTok" />
                          </option>
                          <option value="YouTube">
                            <Translate text="YouTube" />
                          </option>
                          <option value="other">
                            <Translate text="Other" />
                          </option>
                        </Select>
                        <FormErrorMessage>{errors.creator}</FormErrorMessage>
                      </FormControl>
                      <Divider
                        m={1}
                        bg="gray.300"
                        display={{ base: "block", md: "none" }}
                      />
                      <Text fontWeight="bold" color="black" mb={0}>
                        <Translate text="Website or Social Media" />
                      </Text>
                      <FormControl isInvalid={errors.websiteOrInstagram}>
                        <Input
                          placeholder={Translate({
                            text: "URL of your primary platform",
                            as: "string",
                          })}
                          borderColor="gray.300"
                          bg="white"
                          w="100%"
                          value={websiteOrInstagram}
                          onChange={(e) =>
                            setWebsiteOrInstagram(e.target.value)
                          }
                          isRequired
                        />
                        <FormErrorMessage>
                          {errors.websiteOrInstagram}
                        </FormErrorMessage>
                      </FormControl>

                      {/* Button aligned to the right */}
                      <Box w="100%" textAlign="right" mt={3} pr={0}>
                        <Button
                         // bg={isUserLoggedIn ? "#286328" : "gray.400"}
                         bg="#286328"
                          color="white"
                          size="md"
                          borderRadius="lg"
                          transition="transform 0.3s"
                          _hover={isUserLoggedIn ? {
                            transform: "scale(1.1)",
                          } : {}}
                          w={{ base: "100%", md: "auto" }}
                          onClick={handleSubmit}
                          isLoading={loading}
                          cursor={isUserLoggedIn ? "pointer" : "pointer"}
                        >
                          <Translate text={isUserLoggedIn ? "Submit" : "Submit"} />
                        </Button>
                      </Box>
                    </VStack>
                  </Box>
                </Box>
              </Flex>
            </VStack>
          </Flex>

          {/* Section 2: AI Creation (Image on Left, Text on Right) */}
          <Flex
            ref={section2Ref} // Attach the ref to section 2
            // bg="#EBEAF2"
            direction={{ base: "column", md: "row-reverse" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mb={{ base: 0, md: 20 }}
            maxW={{ base: "auto", md: "80vw", lg: "80vw", xl: "80vw" }}
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              // pt={0}
              // pb={0}
              textAlign={{ base: "left", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="top"
              h="100%"
              minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
            >
              <Heading
                fontSize={{ base: "2xl", md: "2xl", lg: "4xl" }}
                // mt={6}
                w={{ base: "100%", md: "100%", lg: "90%" }}
                textAlign="left"
              >
                <Translate text="For Travel" />
                <Text
                  as="span"
                  bgGradient="linear(to-r, #A8E063, #286328)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  {" "}
                  <Translate text="Bloggers & Influencers" />
                </Text>
              </Heading>
              <Box
                mt={4}
                display="flex"
                alignItems="center"
                justifyContent="left"
              >
                <Stack mt={2} spacing={4}>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Grow your audience" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Increase visibility and retention" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Earn and grow your affiliate income long-term" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Make your content even more dynamic" />
                    </Text>
                  </Flex>
                  <Flex alignItems="center">
                    <Icon as={FaCheckCircle} color="#286328" mr={2} />
                    <Text mb={0}>
                      <Translate text="Provide a better planning experience" />
                    </Text>
                  </Flex>
                </Stack>
              </Box>
              {/* <Divider borderColor="gray.400" /> */}

              <Box w="100%" alignContent="center" mt={4}>
                <Button
                  bg="#286328"
                  color="white"
                  transition="transform 0.3s"
                  _hover={{
                    transform: "scale(1.1)",
                  }}
                  borderRadius="lg"
                  w={{ base: "100%", md: "auto" }}
                  size={{ base: "md", md: "md" }}
                  onClick={scrollToSection1}
                >
                  <Translate text="Sign Up" />
                </Button>
              </Box>
            </Box>

            {/* Image Section */}
            <Box
              flex="1"
              // bg="#EBEAF2"
              pl={6}
              pr={6}
              // pt={2}
              // pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent={{ base: "center", md: "center" }}
              h="100%"
              minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
            >
              <Image
                src={aiCreationImage}
                alt="AI Creation"
                boxSize="100%"
                objectFit="cover"
                objectPosition="top"
                borderRadius="40px"
              />
            </Box>
          </Flex>

          {/* Section 3: Vibe Features (Image on Left, Text on Right) */}
          <Flex
            ref={section3Ref}
            direction={{ base: "column-reverse", md: "row-reverse" }}
            alignItems="flex-start" // Align content to the left
            justify="space-between"
            mt={{ base: 20, md: 0 }}
            mb={{ base: 0, md: 20 }}
            maxW="80vw"
            mx="auto"
            h={{ base: "auto", md: "550px", lg: "400px" }} // Responsive height
          >
            {/* <Box
            flex="1"
            // bg="#EBEAF2"
            pt={2}
            pb={2}
            textAlign={{ base: "center", md: "left" }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            // boxShadow="md"
            h={{ base: "100%", md: "100%" }}
            minH={{ base: "auto", md: "100%" }}
          >
            <Image
              src={createrProgramImage}
              alt="AI Creation"
              w="100%" // Constrain max width to the box's width
              h="100%" // Constrain max height to the box's height
              objectFit="contain"
              borderRadius="40px"
            />
          </Box> */}
            <Box
              flex="1"
              // bg="#EBEAF2"
              pl={{ base: 0, md: 6 }}
              pr={{ base: 0, md: 6 }}
              // pt={2}
              // pb={2}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent={{ base: "center", md: "center" }}
              h="100%"
              minH={{ base: "100%", md: "100%" }} // Ensure responsive min-height
            >
              <Image
                src={createrProgramImage}
                alt="AI Creation"
                boxSize="100%"
                objectFit="cover"
                borderRadius="40px"
              />
            </Box>
            {/* Text Section */}
            <Box
              flex="1"
              borderRadius="lg"
              p={6}
              pt={{ base: 0, md: 6 }}
              pr={{ base: 0, md: 6 }}
              pl={{ base: 0, md: 6 }}
              textAlign={{ base: "center", md: "left" }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              h="100%"
              w={{ base: "100%", md: "auto" }}
              // minH={{ base: "300px", md: "100%" }} // Ensure responsive min-height
            >
              <Heading
                fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
                mt={6}
                w={{ base: "100%", md: "100%", lg: "100%" }}
                textAlign="left"
              >
                <Translate text="Join Our" /> {showBreak && <br></br>}
                <Text
                  as="span"
                  bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Content" />{" "}
                </Text>
                {!showBreak && <br></br>}
                <Text
                  as="span"
                  bgGradient="linear(to-r, #286328, #A8E063)" // Changed to the new gradient colors
                  bgClip="text"
                  fontWeight="bold"
                >
                  <Translate text="Creator" />
                </Text>{" "}
                {showBreak && <br></br>}
                <Translate text="Program" />
              </Heading>
              <Stack spacing={2} mt={2} mb={1}>
                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      1
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Apply" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Fill out the form and submit your application." />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>

                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      2
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Set up your Creator Profile" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Once accepted, you’ll receive the required information and can begin creating and uploading your (affiliate) content." />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>

                <Flex
                  bg="white"
                  p={2}
                  borderRadius="xl"
                  boxShadow="md"
                  alignItems="center"
                >
                  <Circle bg="#286328" color="white" size="8" mr={4}>
                    <Text fontWeight="bold" mb={0}>
                      3
                    </Text>
                  </Circle>
                  <Box w="100%">
                    <Accordion
                      allowToggle
                      // defaultIndex={[0]}
                    >
                      <AccordionItem border="none">
                        <AccordionButton p={0} _hover={{ bg: "transparent" }}>
                          <Box
                            flex="1"
                            textAlign="left"
                            fontWeight="bold"
                            fontSize={{ base: "md", md: "lg" }}
                            mr={2}
                          >
                            <Translate text="Start Earning" />
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel
                          pl={0}
                          pb={4}
                          color="gray.600"
                          fontSize="sm"
                          textAlign="left"
                        >
                          <Translate text="Travel for a living and watch your income grow!" />
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </Box>
                </Flex>
                {/* <Divider borderColor="gray.400" /> */}
                <Box w="100%" alignContent="center" mt={4}>
                  <Button
                    // mt={2}
                    bg="#286328"
                    color="white"
                    transition="transform 0.3s"
                    _hover={{
                      transform: "scale(1.1)",
                    }}
                    borderRadius="lg"
                    w={{ base: "100%", md: "auto" }}
                    size={{ base: "md", md: "md" }}
                    onClick={scrollToSection1}
                  >
                    <Translate text="Sign Up" />
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Flex>
          {/* Section 4: FAQ */}
          <FAQCreatorsPage />
        </Box>
        <JoinCommunitySection />
        <Footer />
        <BackToTopButton />
        
        {/* Authentication Modal */}
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={handleAuthModalClose}
          redirectPath="/creators"
        />
      </Box>
    </>
  );
};

export default CreatorPage;
